import { ReactNode } from 'react'
import { Checkbox, Group, IconButton, iconSizes, Table, Text } from '@common/ui'
import { IconChevronDown, IconChevronUp } from '@common/ui/icons'
import SectionShell from './SectionShell'
import { GlobalRow } from './utils'
import { CheckboxWithPolicyBadge, policyMatchesGlobalRow } from './PolicyMatches'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'

type GlobalSectionProps = {
  rows: readonly GlobalRow[]
  open: boolean
  onToggleOpen: () => void
  isChecked: (row: GlobalRow) => boolean
  // Whether the row has scoped (e.g. path/assignment) grants that aren't the All grant — rendered as the
  // indeterminate dash, mirroring the entity-cell checkboxes.
  isIndeterminate: (row: GlobalRow) => boolean
  onToggleRow: (row: GlobalRow) => void
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  expandedRowId: string | null
  onToggleRowExpand: (id: string) => void
  // Optional per-row editor (e.g. the ShareSchoolLogin assignment/path editor). When it returns a node, the
  // row gains an expand toggle that reveals the editor below it; the checkbox keeps toggling the All grant.
  renderRowEditor?: (row: GlobalRow) => ReactNode
}

const borderedCell = { borderRight: '1px solid var(--trellis-color-outline)' }
const subtleBg = 'var(--trellis-color-surface-variant)'

const GlobalSection = ({
  rows,
  open,
  onToggleOpen,
  isChecked,
  isIndeterminate,
  onToggleRow,
  selectedPolicies,
  expandedRowId,
  onToggleRowExpand,
  renderRowEditor,
}: GlobalSectionProps) => {
  const visibleRows = open ? rows : []
  const hiddenCount = rows.length
  const ncols = 2

  return (
    <SectionShell
      title="Global permissions"
      open={open}
      onToggleOpen={onToggleOpen}
      hasFooterToggle={hiddenCount > 0}
      footerLabel={`Show ${hiddenCount} more`}
      ncols={ncols}
      testId="matrix-section-global"
    >
      {open && (
        <Table.Thead>
          <Table.Tr style={{ backgroundColor: subtleBg }}>
            <Table.Th style={{ minWidth: 240, ...borderedCell }}>
              <Text size="sm" fw={600}>
                Action
              </Text>
            </Table.Th>
            <Table.Th style={{ minWidth: 80 }}>
              <Text size="sm" fw={600}>
                Granted
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
      )}
      <Table.Tbody>
        {visibleRows.flatMap((row) => {
          const matches = policyMatchesGlobalRow(selectedPolicies, row.action)
          const editor = renderRowEditor?.(row) ?? null
          const isExpanded = expandedRowId === row.id
          const items = [
            <Table.Tr key={row.id} data-testid={`matrix-row-${row.id}`}>
              <Table.Td style={{ ...borderedCell, backgroundColor: subtleBg }}>{row.label}</Table.Td>
              <Table.Td>
                <Group gap={0} align="center" wrap="nowrap">
                  <CheckboxWithPolicyBadge matches={matches}>
                    <Checkbox
                      size="xs"
                      checked={isChecked(row)}
                      indeterminate={isIndeterminate(row)}
                      onChange={() => onToggleRow(row)}
                      data-testid={`matrix-global-checkbox-${row.id}`}
                      aria-label={`${row.label} permission`}
                    />
                  </CheckboxWithPolicyBadge>
                  {editor && (
                    <IconButton
                      size="sm"
                      onClick={() => onToggleRowExpand(row.id)}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                      data-testid={`matrix-global-expand-${row.id}`}
                    >
                      {isExpanded ? (
                        <IconChevronUp size={iconSizes.xs} stroke={1.5} />
                      ) : (
                        <IconChevronDown size={iconSizes.xs} stroke={1.5} />
                      )}
                    </IconButton>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>,
          ]

          if (editor && isExpanded) {
            items.push(
              <Table.Tr key={`${row.id}-detail`}>
                <Table.Td colSpan={ncols} style={{ backgroundColor: subtleBg, padding: 'var(--trellis-spacing-md)' }}>
                  {editor}
                </Table.Td>
              </Table.Tr>
            )
          }

          return items
        })}
      </Table.Tbody>
    </SectionShell>
  )
}

export default GlobalSection
